import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import SeniorPackageChooser from '../SeniorPackageChooser';
import SeniorPortraitsGallery from '../SeniorPortraitsGallery';
import useRetouchingExtra from '../../../../hooks/useRetouchingExtra';
import { useCustomer } from '../../../../contexts/CustomerContext';
import useBackgrounds from '../../../../hooks/useBackgrounds';
import useImageOrientations from '../../../../hooks/useImageOrientations';
import { OrderPackage } from '../../../../models';
import useAllSeniorPoses from '../../../../hooks/useAllSeniorPoses';
import usePricelist from '../../../../hooks/usePricelist';
import useAddons from '../../../../hooks/useAddons';
import routes from '../../../../routes';
import useQueryString from '../../../../hooks/use-query-string';
import SeniorMultiPose from '../../../PackageBuilders/MultiPose/SeniorMultiPose';
import BuyAllPromo from '../BuyAllPromo';
import useSeniorBuyAllPromoOption from '../../../../hooks/useSeniorBuyAllPromoOption';
import useBuyAllOption from '../../../../hooks/useBuyAllOption';
import shouldShowBuyAllPromo from './buyAllPromoVisibility';
import usePreorderPose from "../../../../hooks/usePreorderPose";
import useHolidayPricelist from "../../../../hooks/useHolidayPricelist";
import usePackagePoses from '../../../../hooks/usePackagePoses';
import GalleryIntro from '../../../GalleryIntro';
import introPoses from '../../../../utils/introPoses';
import { getAppropriateBg } from '../../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';

export default function Builder() {
  // Routing and queries
  const { packageId } = useParams();
  const history = useHistory();
  const { step, opt: optionId } = useQueryString();
  const { preorderPoseId } = useQueryString();

  // Data
  const { customer, setCustomer } = useCustomer();
  const [orderPackage] = useState(new OrderPackage());
  // One-way readiness latch — set once the first real fetch of poses/backgrounds/pricelist
  // completes, then never cleared (see the effect below). Gating on live fetch state would
  // hide the gallery every time STUDENT_IMAGES is invalidated (a yearbook pick).
  const [ready, setReady] = useState(false);
  // useBackgrounds/useAllSeniorPoses set placeholderData: [], so `isLoading` reads false
  // from the first render (TanStack v4 marks the query 'success' the moment placeholder
  // data exists). Use `isFetching`, which DOES reflect the real in-flight fetch, and latch
  // `ready` below (see the effect) so it waits for poses on first load but can't be torn
  // down by a later refetch.
  const { data: backgrounds, isFetching: backgroundsFetching } = useBackgrounds(packageId);
  // EVERY photo from her session — not just the pose numbers the pricelist configures.
  // The gallery and the intro are viewing surfaces: a frame the pricelist forgot to list
  // is still her photo, and she must be able to see it and pick it for the yearbook.
  // The build step keeps usePoses (pricelist config) — SeniorMultiPose fetches its own.
  const { data: poses, isFetching: posesFetching } = useAllSeniorPoses(customer.currentStudent.id, packageId);
  const { data: preorderPose } = usePreorderPose(preorderPoseId)
  const { data: pricelist, isLoading: pricelistLoading } = usePricelist(packageId);
  const { data: packagePoses } = usePackagePoses(packageId);

  // Gallery intro: a short cinematic splash that plays once when the parent lands on
  // the builder (right after package selection), before the yearbook / favorites
  // picker. introDone suppresses a replay when stepping back to this screen within the
  // session — it is session state, NOT a persisted "seen" flag.
  const [introDone, setIntroDone] = useState(false);
  const { data: giftPricelist } = useHolidayPricelist(customer.currentSchool?.id);
  // The dedicated $99.95 "Buy All your photos" promo option for this package, if an
  // admin configured one. Null => no upsell shown.
  const { data: buyAllPromoOption } = useSeniorBuyAllPromoOption(packageId, customer.currentSchool?.id);
  // The REGULAR full-price "All Digital" (buy-all) option for this package. Used to
  // suppress the $99 upsell when the customer just built that same all-images package.
  const { data: allDigitalOption } = useBuyAllOption(packageId, customer.currentSchool?.id);

  // Measure the intro candidates HERE — nothing else does during the intro step (the
  // package cards' AbsolutePosImgs renders later), so without this introPoses reads a cold
  // IMAGE_ORIENTATION_CACHE for the whole splash and a landscape frame (-25.png) leaks into
  // the vertical cards. Feeding introOrientations into the memo below recomputes the list
  // as orientations resolve, so the cards heal to portraits-only.
  const introOrientations = useImageOrientations(
    (poses || []).map((pose) => pose.imageUrl || pose.watermarkUrl).filter(Boolean),
  );

  // Intro cards: each pose's unwatermarked foreground on its best-matched backdrop.
  // MEMOIZED — a fresh array every render changes the photo keys and the preload effect's
  // dependency, which restarts the intro's staggered animation each time a query settles.
  const introPhotos = useMemo(() => introPoses(poses, packagePoses).map((pose) => ({
    foregroundUrl: pose.imageUrl || pose.watermarkUrl,
    backgroundUrl: getAppropriateBg(packagePoses, pose, backgrounds, pricelist)?.imageUrl
      || backgrounds?.[0]?.imageUrl,
  })), [poses, packagePoses, backgrounds, pricelist, introOrientations]);

  // Warm the browser cache for the gallery underneath while the intro plays.
  const introPreloadUrls = useMemo(
    () => introPhotos.flatMap((photo) => [photo.foregroundUrl, photo.backgroundUrl]).filter(Boolean),
    [introPhotos],
  );

  useRetouchingExtra();
  // Senior flow — fetch SENIOR add-ons. useAddons() defaults to elementary,
  // which leaked elementary add-ons (e.g. the $19.95 single digital file) into
  // the senior portrait flow.
  const { data: addons } = useAddons(false);

  // Where the flow goes once the built package is done (add-ons / retouching /
  // holiday gifts). The Buy All upsell hands control back here via onContinue.
  const goAfterBuild = () => {
    if (giftPricelist) history.push(routes.holiday.builder({ optional: true, o: orderPackage.id, pid: orderPackage?.packageId }))
    else if (addons.length === 0) history.push(routes.retouching(orderPackage.id));
    else history.push(routes.seniors.addons(orderPackage.id));
  };

  const onSave = () => {
    setCustomer({ ...customer, currentOrderPackage: orderPackage });

    // Senior "Buy All your photos $99.95" upsell — shown AFTER the package is built
    // and BEFORE add-ons whenever a promo option is configured for this package and
    // the customer hasn't already bought all their images. Also skipped when the
    // customer just built the full-price "All Digital" package (the same all-images
    // product) — offering $99 for what they bought at full price is confusing.
    // NOTE: giftPricelist is a SCHOOL-level holiday pricelist, NOT a reason to skip —
    // gift-enabled schools still get the upsell, then flow on to the gifts step via
    // goAfterBuild. Purely additive: it adds a SEPARATE buy-all OrderPackage and never
    // touches the package just built (or currentOrderPackage, still set above).
    const showBuyAllPromo = shouldShowBuyAllPromo({
      buyAllPromoOption,
      hasSeniorBuyAll: customer.cart?.hasSeniorBuyAll,
      builtOptionId: optionId,
      allDigitalOption,
    });

    if (showBuyAllPromo) {
      history.push(routes.seniors.packageBuilder(packageId, { step: 'buy-all-promo' }));
    } else {
      goAfterBuild();
    }
  };

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  // Flip `ready` the first time poses/backgrounds/pricelist have all finished fetching and
  // backgrounds are present. isFetching (not isLoading — masked by placeholderData) is the
  // honest first-load signal; the latch never clears, so a refetch after a yearbook pick
  // can't tear the gallery down. pricelist has no placeholderData, so its isLoading is fine.
  useEffect(() => {
    if (!ready && !posesFetching && !backgroundsFetching && !pricelistLoading && backgrounds.length > 0) {
      setReady(true);
    }
  }, [ready, posesFetching, backgroundsFetching, pricelistLoading, backgrounds]);

  if (!(packageId && customer.currentSchool && customer.currentStudent)) return null;

  // ---- Gallery intro (plays between package selection and the yearbook/favorites step) ----
  // Class year comes from the pricelist season ("seniors2027" -> "2027").
  const gradYearMatch = pricelist?.season?.match(/(\d{4})/);
  const classYear = gradYearMatch ? gradYearMatch[1] : null;

  // Show on first entry (the yearbook-pose step), once per session.
  const onFirstEntry = (!step || step === 'yearbook-pose');
  // Gate on introPhotos being non-empty (as MultiPosePackageBuilder does): a student whose
  // frame numbers don't intersect the pricelist's package_poses (walk-ins, custom
  // numbering) would otherwise get a photo-less black intro for the full fallback timeout.
  const showIntro = onFirstEntry && !introDone && introPhotos.length > 0;

  // The senior flow LEADS with the yearbook-pose pick (all photos shown), then packages,
  // then the builder. The chosen yearbook pose (Pose#is_yearbook_pose) is the hero image
  // for the package upsell and sorts first in the build step.
  const yearbookPose = (poses || []).find((pose) => pose.isYearbookPose === true);
  const hasYearbookPose = !!yearbookPose;

  // Required-yearbook gate: a senior cannot reach packages/build/checkout without a
  // confirmed yearbook pose. If they land on select-package or build without one, send
  // them back to the yearbook step. Use <Redirect> rather than history.replace so the
  // navigation is render-safe (no state update in render). Only once the poses are in —
  // mid-fetch, `poses` is empty and every senior would look like they have no yearbook pose.
  if (ready && (step === 'select-package' || step === 'build') && !hasYearbookPose) {
    return <Redirect to={routes.seniors.packageBuilder(packageId, { step: 'yearbook-pose' })} />;
  }

  return (
    <div>
      {/* Full-screen intro overlay; when it finishes (introDone) it unmounts and reveals
          the yearbook picker already rendered underneath — no flash.

          It MUST stay at this one position in the tree, rendered on every pass. Returning
          it as a bare root while data loads (and nesting it here afterwards) moves it in
          the element tree, so React unmounts/remounts it and its animation restarts from
          the top — and because the four queries settle at different moments, that happened
          3-4 times in a row. Gate the CONTENT below on `ready`, never the intro's position. */}
      {showIntro && (
        <GalleryIntro
          studentFirstName={customer.currentStudent.firstName}
          eyebrow={classYear ? `Class of ${classYear}` : null}
          allPhotos={introPhotos}
          preloadUrls={introPreloadUrls}
          onComplete={() => setIntroDone(true)}
        />
      )}
      {ready && (!step || step === 'yearbook-pose') && (
        <SeniorPortraitsGallery
          poses={poses}
          backgrounds={backgrounds}
          packageId={packageId}
          onBuildPackage={() => history.push(routes.seniors.packageBuilder(packageId, { step: 'select-package' }))}
        />
      )}
      {ready && step === 'select-package' && pricelist && (
        <SeniorPackageChooser
          packageId={packageId}
          onSelect={(option) => history.push(
            routes.seniors.packageBuilder(packageId, { opt: option.id, step: 'build' }),
          )}
          // The $0 yearbook line is already in the cart from the gallery step, so a senior
          // who wants nothing else goes straight to review/checkout.
          onSkip={() => history.push(routes.review())}
        />
      )}
      {ready && step === 'build' && (
        <SeniorMultiPose
          cartId={customer.cart.id}
          packageId={packageId}
          schoolId={customer.currentSchool.id}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          onSave={onSave}
          favoriteIds={yearbookPose ? [yearbookPose.id] : []}
          orderPackage={orderPackage}
        />
      )}
      {ready && step === 'buy-all-promo' && (
        <BuyAllPromo
          packageId={packageId}
          printOrderPackageId={orderPackage.id}
          onContinue={goAfterBuild}
        />
      )}
    </div>
  );
}
